import { AuthStorage } from "utils/auth-storage";
import React, { useState } from "react";
import { Button, Modal, Upload, message } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useParams } from "react-router-dom";
import { WarehouseServices } from "apis/Services/WarehouseService";
import ReviewFile from "./review-file";

const { Dragger } = Upload;

const normalizeImportedInventoryRow = (row = {}) => ({
  ...row,
  materialName: row.materialName ?? row["Material Name"] ?? "",
  sku: row.sku ?? row.SKU ?? "",
  itemVendor: row.itemVendor ?? row.vendor ?? row.Vendor ?? "",
  category: row.category ?? row.Category ?? "",
  subcategory: row.subcategory ?? row.Subcategory ?? "",
  description: row.description ?? row.Description ?? "",
  unitPrice: row.unitPrice ?? row["Unit Price"] ?? "",
  unit:
    row.unit !== undefined && row.unit !== null
      ? String(row.unit).trim()
      : row.Unit !== undefined && row.Unit !== null
        ? String(row.Unit).trim()
        : "",
  currentStock: row.currentStock ?? row["Current Stock"] ?? "",
  minStock: row.minStock ?? row["Min Stock"] ?? "",
  maxStock: row.maxStock ?? row["Max Stock"] ?? "",
  storageLocation: row.storageLocation ?? row["Storage Location"] ?? "",
  itemBay: row.itemBay ?? row.Bay ?? "",
  itemShelf: row.itemShelf ?? row.Shelf ?? "",
  itemRow: row.itemRow ?? row.Row ?? "",
});

const normalizeImportedInventoryRows = (rows) =>
  Array.isArray(rows) ? rows.map(normalizeImportedInventoryRow) : [];

export default function ImportInventory({
  categoryOptions,
  subcategoryOptions,
  locationsOptions,
  unitsOptions,
  setShouldReload = () => {},
}) {
  const { company_id } = useParams();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [acceptedData, setAcceptedData] = useState([]);
  const [rejectedData, setRejectedData] = useState([]);

  const [fileList, setFileList] = useState([]);

  const showModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCancel = () => {
    setIsUploadModalOpen(false);
    setIsReviewModalOpen(false);
    setAcceptedData([]);
    setRejectedData([]);
    setFileList([]);
  };

  const handleUpload = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Get header row explicitly
        const header = XLSX.utils.sheet_to_json(worksheet, {
          header: 1, // first row as array
          range: 0,
        })[0];

        // Expected columns (must exactly match these)
        const expectedColumns = [
          "Material Name",
          "SKU",
          "Vendor",
          "Category",
          "Subcategory",
          "Description",
          "Unit Price",
          "Unit",
          "Current Stock",
          "Min Stock",
          "Max Stock",
          "Storage Location",
          "Bay",
          "Shelf",
          "Row",
        ];

        // Validate column names
        const missing = expectedColumns.filter((col) => !header.includes(col));
        const extra = header.filter((col) => !expectedColumns.includes(col));

        if (missing.length > 0 || extra.length > 0) {
          message.error("File doesn't match the template columns.");
          console.error("Missing columns:", missing);
          console.error("Unexpected columns:", extra);
          return;
        }

        // Convert to JSON with `defval` to fill empty cells
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "", // fill empty cells with empty string
        });

        // Normalize each row (in case SheetJS skips missing columns)
        const normalizedData = jsonData.map((row) => {
          const normalizedRow = {};
          expectedColumns.forEach((col) => {
            normalizedRow[col] = row[col] ?? "";
          });
          return normalizedRow;
        });

        // Prepare request
        let code = AuthStorage.getItem("token");
        let email = AuthStorage.getItem("email");

        let ReqObj = {
          company: company_id,
          code,
          loginEmail: email,
          saveFlag: "0",
          inventoryRows: normalizedData,
        };

        WarehouseServices.uploadWarehouseItems(ReqObj)
          .then((res) => {
            message.success("File processed successfully!");
            console.log("RESPONSE:", res);
            setAcceptedData(normalizeImportedInventoryRows(res.validItems));
            setRejectedData(normalizeImportedInventoryRows(res.invalidItems));
            setIsReviewModalOpen(true);
          })
          .catch((err) => {
            console.error(err);
            // message.error("Error uploading file.");
            message.error(err?.message);
          });

        console.log("Converted JSON Data:", normalizedData);

        // Close modal after success
        setTimeout(() => {
          setIsUploadModalOpen(false);
          setFileList([]);
        }, 1000);
      } catch (error) {
        console.error("Error processing file:", error);
        message.error("Error processing file. Please try again.");
      }
    };

    reader.readAsArrayBuffer(file);
    return false;
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    fileList,
    accept: ".xlsx,.xls,.csv",
    beforeUpload: (file) => {
      const isExcelOrCsv =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv";

      if (!isExcelOrCsv) {
        message.error("You can only upload Excel or CSV files!");
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      handleUpload(file);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="text-center">
        <Button
          type="primary"
          icon={<UploadOutlined />}
          size="large"
          onClick={showModal}
        >
          Import
        </Button>

        <Modal
          title="Upload File"
          centered
          open={isUploadModalOpen}
          onCancel={handleCancel}
          footer={null}
          width={600}
        >
          <div className="py-1">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for Excel (.xlsx, .xls) and CSV files
              </p>
            </Dragger>

            <div className="mt-4 text-center">
              <p>
                <a
                  href="/KoncurentInventoryTemplate.csv"
                  download="KoncurentInventoryTemplate.csv"
                >
                  Download Template
                </a>
              </p>
            </div>
          </div>
        </Modal>

        <Modal
          title="Review & finalize"
          centered
          open={isReviewModalOpen}
          onCancel={handleCancel}
          footer={null}
          width={1500}
        >
          <div className="">
            <ReviewFile
              rejectedData={rejectedData}
              acceptedData={acceptedData}
              handleCancel={handleCancel}
              setAcceptedData={setAcceptedData}
              setRejectedData={setRejectedData}
              categoryOptions={categoryOptions}
              subcategoryOptions={subcategoryOptions}
              locationsOptions={locationsOptions}
              unitsOptions={unitsOptions}
              setShouldReload={setShouldReload}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}
